'use client';

import { c as _c } from "react/compiler-runtime";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/comps/primitives/tooltip';
import { ChipName } from '@/domains/pages/ai-proficiency/components/chip-name';
import { LadderLevelBar } from '@/domains/pages/ai-proficiency/components/ladder-level-bar';
import { LevelShareDonut } from '@/domains/pages/ai-proficiency/components/level-share-donut';
import { POWER_BAR_MARKER_LINE_WIDTH, POWER_BAR_TICK_COLOR } from '@/domains/pages/shared/power-bar-shared';
import { AIFI_LEVEL_COLORS, AIFI_LEVEL_GRID_TEMPLATE_COLUMNS, AIFI_LEVEL_DESCRIPTIONS, AIFI_LEVELS, type AifiLevel } from '@/domains/pages/ai-proficiency/constants';
import { AIFI_DATA_NUMBER_CLASS } from '@/domains/pages/ai-proficiency/typography';
import type { LevelBenchmarkShare } from '@/domains/pages/ai-proficiency/fixtures/benchmarks';
import type { LevelSnapshot } from '@/domains/pages/ai-proficiency/utils/map-aggregates';
import { computeBestTeamShareAtLevel, isLevelShareLowerBetter, isSelectedBestAtLevel, partitionTeamsForLadder } from '@/domains/pages/ai-proficiency/utils/ladder';
import { formatUserCount } from '@/domains/pages/ai-proficiency/utils/format-users';
import { formatScore } from '@/utils/format.util';
import { cn } from '@/utils/shad';
import { Info, Users } from 'lucide-react';
import { useMemo } from 'react';
type LadderComparisonMode = 'topQuartile' | 'bestSegment' | 'none' | 'teams';

/**
 * Every column shares these row tracks through `grid-rows-subgrid`, so the share, description,
 * bar, and teams rows stay aligned however long a level description wraps.
 */
const LADDER_ROW_TRACKS_CLASS = 'grid-rows-[auto_auto_auto_1fr]';
const LADDER_COLUMN_ROWS_CLASS = 'row-span-4 grid-rows-subgrid';

/** Legend tick height, short enough to sit on a card header line. */
const LEGEND_TICK_HEIGHT_PX = 11;
type LevelBenchmarkWithBest = LevelBenchmarkShare & {
  bestSegmentName?: string;
};
type ProficiencyLadderProps = {
  levels: LevelSnapshot[];
  totalUsers: number;
  levelBenchmarks: Record<AifiLevel, LevelBenchmarkWithBest>;
  comparison?: LadderComparisonMode;
  segmentNoun?: string;
  segmentNounPlural?: string;
  subjectLabel?: string;
  orgLevels?: LevelSnapshot[];
};
function LadderColumn(t0) {
  const $ = _c(88);
  const {
    level,
    snapshot,
    totalUsers,
    benchmarkShare,
    comparison,
    segmentNoun,
    segmentNounPlural,
    subjectLabel,
    orgSnapshot,
    levelBenchmark,
    isFirst,
    isLast
  } = t0;
  const color = AIFI_LEVEL_COLORS[level];
  const userCount = snapshot?.userCount ?? 0;
  const sharePercent = snapshot?.sharePercent ?? 0;
  const isBestAtLevel = comparison === "bestSegment" && isSelectedBestAtLevel(level, sharePercent, benchmarkShare);
  const showBar = comparison !== "teams";
  const showTeams = comparison === "topQuartile" || comparison === "teams";
  const displayBenchmarkShare = isBestAtLevel ? sharePercent : benchmarkShare;
  snapshot?.teams;
  let t1;
  if ($[0] !== snapshot?.teams) {
    t1 = snapshot?.teams ?? [];
    $[0] = snapshot?.teams;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== t1) {
    t2 = partitionTeamsForLadder(t1);
    $[2] = t1;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const displayTeams = t2;
  const shareOf = orgSnapshot != null || subjectLabel !== "Organization" ? `the ${subjectLabel}` : "workforce";
  let t3;
  if ($[4] !== benchmarkShare || $[5] !== comparison || $[6] !== level || $[7] !== levelBenchmark?.topQuartileShare || $[8] !== orgSnapshot?.sharePercent || $[9] !== segmentNoun || $[10] !== snapshot?.teams || $[11] !== totalUsers) {
    t3 = comparison === "bestSegment" ? {
      orgSharePercent: orgSnapshot?.sharePercent ?? 0,
      bestSegmentShare: levelBenchmark?.topQuartileShare ?? benchmarkShare,
      bestSegmentLabel: `Best ${segmentNoun ?? "segment"}`
    } : {
      topQuartileShare: levelBenchmark?.topQuartileShare ?? benchmarkShare,
      bestSegmentShare: computeBestTeamShareAtLevel(snapshot?.teams ?? [], totalUsers, level),
      bestSegmentLabel: "Best team"
    };
    $[4] = benchmarkShare;
    $[5] = comparison;
    $[6] = level;
    $[7] = levelBenchmark?.topQuartileShare;
    $[8] = orgSnapshot?.sharePercent;
    $[9] = segmentNoun;
    $[10] = snapshot?.teams;
    $[11] = totalUsers;
    $[12] = t3;
  } else {
    t3 = $[12];
  }
  const barTooltip = t3;
  let t4;
  let t5;
  let t6;
  let t7;
  let t8;
  if ($[13] !== barTooltip || $[14] !== color || $[15] !== comparison || $[16] !== displayBenchmarkShare || $[17] !== isFirst || $[18] !== isLast || $[19] !== level || $[20] !== levelBenchmark?.bestSegmentName || $[21] !== segmentNoun || $[22] !== segmentNounPlural || $[23] !== shareOf || $[24] !== sharePercent || $[25] !== showBar || $[26] !== subjectLabel || $[27] !== userCount) {
    const gutter = cn(isFirst ? "pl-8" : "pl-6", isLast ? "pr-8" : "pr-6");
    if ($[33] === Symbol.for("react.memo_cache_sentinel")) {
      t5 = cn("grid min-w-0 border-r border-lightborder last:border-r-0", LADDER_COLUMN_ROWS_CLASS);
      $[33] = t5;
    } else {
      t5 = $[33];
    }
    const t9 = cn("flex min-w-0 items-center gap-3 pt-6", gutter);
    let t10;
    if ($[34] !== color || $[35] !== level || $[36] !== shareOf || $[37] !== sharePercent || $[38] !== userCount) {
      t10 = <LevelShareDonut sharePercent={sharePercent} color={color} label={level} userCount={userCount} shareOf={shareOf} CenterIcon={Users} />;
      $[34] = color;
      $[35] = level;
      $[36] = shareOf;
      $[37] = sharePercent;
      $[38] = userCount;
      $[39] = t10;
    } else {
      t10 = $[39];
    }
    let t11;
    if ($[40] === Symbol.for("react.memo_cache_sentinel")) {
      t11 = cn("block text-2xl leading-none font-semibold text-foreground", AIFI_DATA_NUMBER_CLASS);
      $[40] = t11;
    } else {
      t11 = $[40];
    }
    let t12;
    if ($[41] !== sharePercent) {
      t12 = formatScore(sharePercent, {
        decimals: 1
      });
      $[41] = sharePercent;
      $[42] = t12;
    } else {
      t12 = $[42];
    }
    let t13;
    if ($[43] === Symbol.for("react.memo_cache_sentinel")) {
      t13 = <span className="text-base">%</span>;
      $[43] = t13;
    } else {
      t13 = $[43];
    }
    let t14;
    if ($[44] !== t12) {
      t14 = <span className={t11}>{t12}{t13}</span>;
      $[44] = t12;
      $[45] = t14;
    } else {
      t14 = $[45];
    }
    let t15;
    if ($[46] === Symbol.for("react.memo_cache_sentinel")) {
      t15 = cn("mt-1.5 block truncate text-[13px] tracking-[0.18em] uppercase", AIFI_DATA_NUMBER_CLASS);
      $[46] = t15;
    } else {
      t15 = $[46];
    }
    let t16;
    if ($[47] !== color) {
      t16 = {
        color
      };
      $[47] = color;
      $[48] = t16;
    } else {
      t16 = $[48];
    }
    let t17;
    if ($[49] !== level || $[50] !== t16) {
      t17 = <span className={t15} style={t16}>{level}</span>;
      $[49] = level;
      $[50] = t16;
      $[51] = t17;
    } else {
      t17 = $[51];
    }
    let t18;
    if ($[52] !== t14 || $[53] !== t17) {
      t18 = <span className="min-w-0">{t14}{t17}</span>;
      $[52] = t14;
      $[53] = t17;
      $[54] = t18;
    } else {
      t18 = $[54];
    }
    t6 = <div className={t9}>{t10}{t18}</div>;
    const t19 = AIFI_LEVEL_DESCRIPTIONS[level];
    let t20;
    if ($[55] !== t19) {
      t20 = <p className="text-xs leading-snug text-muted-foreground">{t19}</p>;
      $[55] = t19;
      $[56] = t20;
    } else {
      t20 = $[56];
    }
    t7 = <div className={cn("min-w-0 pt-2", gutter)}>{t20}</div>;
    let t21;
    if ($[57] !== barTooltip || $[58] !== color || $[59] !== comparison || $[60] !== displayBenchmarkShare || $[61] !== level || $[62] !== segmentNoun || $[63] !== sharePercent || $[64] !== showBar || $[65] !== subjectLabel) {
      t21 = showBar ? <LadderLevelBar level={level} sharePercent={sharePercent} benchmarkShare={comparison === "none" ? sharePercent : displayBenchmarkShare} fillColor={color} comparison={comparison === "none" ? "topQuartile" : comparison} showBenchmark={comparison !== "none"} subjectLabel={subjectLabel} segmentNoun={segmentNoun} barTooltip={barTooltip} /> : null;
      $[57] = barTooltip;
      $[58] = color;
      $[59] = comparison;
      $[60] = displayBenchmarkShare;
      $[61] = level;
      $[62] = segmentNoun;
      $[63] = sharePercent;
      $[64] = showBar;
      $[65] = subjectLabel;
      $[66] = t21;
    } else {
      t21 = $[66];
    }
    let t22;
    if ($[67] !== comparison || $[68] !== displayBenchmarkShare || $[69] !== level || $[70] !== levelBenchmark?.bestSegmentName || $[71] !== segmentNoun || $[72] !== segmentNounPlural || $[73] !== showBar) {
      t22 = showBar ? <ProficiencyLadderLegend className="mt-2.5" level={level} comparison={comparison} segmentNoun={segmentNoun} segmentNounPlural={segmentNounPlural} benchmarkShare={displayBenchmarkShare} bestSegmentName={levelBenchmark?.bestSegmentName} /> : null;
      $[67] = comparison;
      $[68] = displayBenchmarkShare;
      $[69] = level;
      $[70] = levelBenchmark?.bestSegmentName;
      $[71] = segmentNoun;
      $[72] = segmentNounPlural;
      $[73] = showBar;
      $[74] = t22;
    } else {
      t22 = $[74];
    }
    t8 = <div className={cn("min-w-0", showBar && "pt-4", gutter)}>{t21}{t22}</div>;
    t4 = cn("min-w-0 pb-4", gutter);
    $[13] = barTooltip;
    $[14] = color;
    $[15] = comparison;
    $[16] = displayBenchmarkShare;
    $[17] = isFirst;
    $[18] = isLast;
    $[19] = level;
    $[20] = levelBenchmark?.bestSegmentName;
    $[21] = segmentNoun;
    $[22] = segmentNounPlural;
    $[23] = shareOf;
    $[24] = sharePercent;
    $[25] = showBar;
    $[26] = subjectLabel;
    $[27] = userCount;
    $[28] = t4;
    $[29] = t5;
    $[30] = t6;
    $[31] = t7;
    $[32] = t8;
  } else {
    t4 = $[28];
    t5 = $[29];
    t6 = $[30];
    t7 = $[31];
    t8 = $[32];
  }
  let t9;
  if ($[75] !== color || $[76] !== displayTeams || $[77] !== showTeams) {
    t9 = showTeams && displayTeams.length > 0 ? <div className="pt-5"><div className={cn("text-[10px] tracking-[0.18em] text-muted-foreground uppercase", AIFI_DATA_NUMBER_CLASS)}>Teams</div><div className="mt-2 flex flex-wrap gap-2">{displayTeams.map(team => <span key={team.team} className="flex max-w-full items-center gap-1 rounded-full border border-border bg-secondary/40 px-2.5 py-1 text-xs text-muted-foreground"><ChipName name={team.team} className="max-w-24" /><span className="shrink-0">·</span><span className={cn("shrink-0 font-medium", AIFI_DATA_NUMBER_CLASS)} style={{
            color
          }}>{formatUserCount(team.count)}</span></span>)}</div></div> : null;
    $[75] = color;
    $[76] = displayTeams;
    $[77] = showTeams;
    $[78] = t9;
  } else {
    t9 = $[78];
  }
  let t10;
  if ($[79] !== t4 || $[80] !== t9) {
    t10 = <div className={t4}>{t9}</div>;
    $[79] = t4;
    $[80] = t9;
    $[81] = t10;
  } else {
    t10 = $[81];
  }
  let t11;
  if ($[82] !== t10 || $[83] !== t5 || $[84] !== t6 || $[85] !== t7 || $[86] !== t8) {
    t11 = <div className={t5}>{t6}{t7}{t8}{t10}</div>;
    $[82] = t10;
    $[83] = t5;
    $[84] = t6;
    $[85] = t7;
    $[86] = t8;
    $[87] = t11;
  } else {
    t11 = $[87];
  }
  return t11;
}
type ProficiencyLadderLegendProps = {
  level: AifiLevel;
  comparison?: LadderComparisonMode;
  segmentNoun?: string;
  segmentNounPlural?: string;
  /** Share the tick sits at, so the tooltip can quote it. */
  benchmarkShare?: number;
  /** Name of the leading segment at this group, when the data carries it. */
  bestSegmentName?: string;
  className?: string;
};
type BestSegmentTooltipInput = {
  level: AifiLevel;
  segmentNoun: string;
  segmentNounPlural: string;
  benchmarkShare?: number;
  bestSegmentName?: string;
};

/**
 * Group-specific copy for the tick: at Expert and Practitioner the leader holds the
 * largest share, at Beginner and Novice the smallest.
 */
function buildBestSegmentTooltip({
  level,
  segmentNoun,
  segmentNounPlural,
  benchmarkShare,
  bestSegmentName
}: BestSegmentTooltipInput): string {
  const superlative = isLevelShareLowerBetter(level) ? 'lowest' : 'highest';
  const share = benchmarkShare != null ? `${formatScore(benchmarkShare, {
    decimals: 1
  })}%` : null;
  const subject = bestSegmentName ? `${bestSegmentName} has the ${superlative} share of ${level}s of all ${segmentNounPlural}` : `The ${segmentNoun} with the ${superlative} share of ${level}s of all ${segmentNounPlural}`;
  return `${subject}${share ? `, at ${share}` : ''}.`;
}

/** Names the benchmark tick drawn on this group's bar (e.g. "Best team"). */
export function ProficiencyLadderLegend(t0) {
  const $ = _c(21);
  const {
    level,
    comparison: t1,
    segmentNoun,
    segmentNounPlural,
    benchmarkShare,
    bestSegmentName,
    className
  } = t0;
  const comparison = t1 === undefined ? "bestSegment" : t1;
  if (comparison !== "bestSegment") {
    return null;
  }
  const noun = segmentNoun ?? "segment";
  const nounPlural = segmentNounPlural ?? `${noun}s`;
  const label = `Best ${noun}`;
  let t2;
  if ($[0] !== benchmarkShare || $[1] !== bestSegmentName || $[2] !== level || $[3] !== noun || $[4] !== nounPlural) {
    t2 = buildBestSegmentTooltip({
      level,
      segmentNoun: noun,
      segmentNounPlural: nounPlural,
      benchmarkShare,
      bestSegmentName
    });
    $[0] = benchmarkShare;
    $[1] = bestSegmentName;
    $[2] = level;
    $[3] = noun;
    $[4] = nounPlural;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  const tooltip = t2;
  let t3;
  if ($[6] !== className) {
    t3 = cn("flex items-center gap-1.5 text-xs text-muted-foreground", className);
    $[6] = className;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  let t4;
  if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = <span className="block shrink-0 rounded-full" style={{
      width: POWER_BAR_MARKER_LINE_WIDTH,
      height: LEGEND_TICK_HEIGHT_PX,
      backgroundColor: POWER_BAR_TICK_COLOR
    }} aria-hidden={true} />;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  const t5 = `How ${label} is picked at ${level}`;
  let t6;
  if ($[9] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = <Info className="size-3.5" />;
    $[9] = t6;
  } else {
    t6 = $[9];
  }
  let t7;
  if ($[10] !== t5) {
    t7 = <TooltipTrigger asChild={true}><button type="button" aria-label={t5} className="text-muted-foreground hover:text-foreground">{t6}</button></TooltipTrigger>;
    $[10] = t5;
    $[11] = t7;
  } else {
    t7 = $[11];
  }
  let t8;
  if ($[12] !== tooltip) {
    t8 = <TooltipContent variant="primary" className="max-w-60 text-xs">{tooltip}</TooltipContent>;
    $[12] = tooltip;
    $[13] = t8;
  } else {
    t8 = $[13];
  }
  let t9;
  if ($[14] !== t7 || $[15] !== t8) {
    t9 = <TooltipProvider delayDuration={100}><Tooltip>{t7}{t8}</Tooltip></TooltipProvider>;
    $[14] = t7;
    $[15] = t8;
    $[16] = t9;
  } else {
    t9 = $[16];
  }
  let t10;
  if ($[17] !== label || $[18] !== t3 || $[19] !== t9) {
    t10 = <div className={t3} data-testid="proficiency-ladder-legend">{t4}{label}{t9}</div>;
    $[17] = label;
    $[18] = t3;
    $[19] = t9;
    $[20] = t10;
  } else {
    t10 = $[20];
  }
  return t10;
}
export function ProficiencyLadder(t0) {
  const $ = _c(17);
  const {
    levels,
    totalUsers,
    levelBenchmarks,
    comparison: t1,
    segmentNoun,
    segmentNounPlural,
    subjectLabel: t2,
    orgLevels
  } = t0;
  const comparison = t1 === undefined ? "topQuartile" : t1;
  const subjectLabel = t2 === undefined ? "Organization" : t2;
  let t3;
  if ($[0] !== levels) {
    t3 = new Map(levels.map(_temp));
    $[0] = levels;
    $[1] = t3;
  } else {
    t3 = $[1];
  }
  const levelMap = t3;
  let t4;
  if ($[2] !== orgLevels) {
    t4 = new Map((orgLevels ?? []).map(_temp2));
    $[2] = orgLevels;
    $[3] = t4;
  } else {
    t4 = $[3];
  }
  const orgLevelMap = t4;
  let t5;
  let t6;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = cn("grid items-stretch divide-x divide-lightborder", LADDER_ROW_TRACKS_CLASS);
    t6 = {
      gridTemplateColumns: AIFI_LEVEL_GRID_TEMPLATE_COLUMNS
    };
    $[4] = t5;
    $[5] = t6;
  } else {
    t5 = $[4];
    t6 = $[5];
  }
  let t7;
  if ($[6] !== comparison || $[7] !== levelBenchmarks || $[8] !== levelMap || $[9] !== orgLevelMap || $[10] !== segmentNoun || $[11] !== segmentNounPlural || $[12] !== subjectLabel || $[13] !== totalUsers) {
    t7 = AIFI_LEVELS.map((level_1, index) => <LadderColumn key={level_1} isFirst={index === 0} isLast={index === AIFI_LEVELS.length - 1} level={level_1} snapshot={levelMap.get(level_1)} totalUsers={totalUsers} benchmarkShare={levelBenchmarks[level_1]?.topQuartileShare ?? 0} comparison={comparison} segmentNoun={segmentNoun} segmentNounPlural={segmentNounPlural} subjectLabel={subjectLabel} orgSnapshot={orgLevelMap.get(level_1)} levelBenchmark={levelBenchmarks[level_1]} />);
    $[6] = comparison;
    $[7] = levelBenchmarks;
    $[8] = levelMap;
    $[9] = orgLevelMap;
    $[10] = segmentNoun;
    $[11] = segmentNounPlural;
    $[12] = subjectLabel;
    $[13] = totalUsers;
    $[14] = t7;
  } else {
    t7 = $[14];
  }
  let t8;
  if ($[15] !== t7) {
    t8 = <div className={t5} style={t6} data-testid="proficiency-ladder">{t7}</div>;
    $[15] = t7;
    $[16] = t8;
  } else {
    t8 = $[16];
  }
  return t8;
}
function _temp2(level_0) {
  return [level_0.level, level_0];
}
function _temp(level) {
  return [level.level, level];
}